import { useId } from 'react';
import { clsx } from '../../../ds/clsx';
import { Checkbox } from '../../../ds/components';
import type { Signature, SignatureBody } from '../../onboarding/user/types';
import styles from './SignatureEditor.module.css';

/**
 * The signature editor of Onboarding Nutzer 09 / M7 and the „Signatur bearbeiten" dialog of Einstellungen 01:
 * the tabs „Firmenvorlage" / „Frei schreiben", the template lines (filled by the server) or the free text, the
 * company footer, the two checkboxes („Profilbild einbetten" not on the phone, M7).
 */

export interface SignatureLines {
  lines: string[];
  footer: string | null;
}

/** What the signature shows for the current (unsaved) choice. */
export function signatureLines(signature: Signature, value: SignatureBody): SignatureLines {
  const lines =
    value.mode === 'template'
      ? signature.template.lines
      : value.text
          .split('\n')
          .map((line) => line.trimEnd())
          .filter((line, index, all) => line !== '' || (index > 0 && index < all.length - 1));
  return { lines, footer: value.mandatoryDetails ? signature.template.footer : null };
}

export function bodyOf(signature: Signature): SignatureBody {
  return {
    mode: signature.mode,
    text: signature.mode === 'free' ? signature.text : signature.text || signature.template.lines.join('\n'),
    mandatoryDetails: signature.mandatoryDetails,
    embedImage: signature.embedImage,
  };
}

/** The signature block (name bold, lines, hairline and the footer in small type). */
export function SignatureBlock({
  lines,
  footer,
  variant = 'editor',
  className,
}: SignatureLines & { variant?: 'editor' | 'card' | 'mobile'; className?: string | undefined }) {
  const [first, ...rest] = lines;
  return (
    <div
      className={clsx(styles.block, variant === 'editor' ? styles.blockEditor : styles[variant], className)}
    >
      {first !== undefined ? <div className={styles.name}>{first}</div> : null}
      {rest.map((line, index) => (
        <div key={index} className={/\d/.test(line) ? styles.numeric : undefined}>
          {line}
        </div>
      ))}
      {footer ? <div className={styles.footer}>{footer}</div> : null}
    </div>
  );
}

export interface SignatureEditorProps {
  signature: Signature;
  value: SignatureBody;
  onChange: (value: SignatureBody) => void;
  variant: 'wizard' | 'dialog' | 'mobile';
  error?: string | null | undefined;
}

export function SignatureEditor({ signature, value, onChange, variant, error }: SignatureEditorProps) {
  const textId = useId();
  const errorId = `${textId}-error`;
  const shown = signatureLines(signature, value);
  const mobile = variant === 'mobile';
  const tabs: Array<{ mode: SignatureBody['mode']; label: string }> = [
    { mode: 'template', label: 'Firmenvorlage' },
    { mode: 'free', label: 'Frei schreiben' },
  ];

  function select(mode: SignatureBody['mode']) {
    if (mode === value.mode) return;
    onChange({
      ...value,
      mode,
      text: mode === 'free' && value.text.trim() === '' ? signature.template.lines.join('\n') : value.text,
    });
  }

  return (
    <div className={clsx(styles.editor, mobile && styles.editorMobile)}>
      <div className={clsx(styles.tabRow, mobile && styles.tabRowMobile)}>
        <div role="tablist" aria-label="Signatur" className={clsx(styles.tabs, mobile && styles.tabsMobile)}>
          {tabs.map((tab) => (
            <button
              key={tab.mode}
              type="button"
              role="tab"
              aria-selected={value.mode === tab.mode}
              className={clsx(styles.tab, value.mode === tab.mode && styles.tabActive)}
              onClick={() => select(tab.mode)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {!mobile && value.mode === 'template' ? (
          <span className={styles.tabNote}>Platzhalter werden automatisch gefüllt</span>
        ) : null}
      </div>
      {value.mode === 'template' ? (
        <SignatureBlock lines={shown.lines} footer={shown.footer} variant={mobile ? 'mobile' : 'editor'} />
      ) : (
        <div className={styles.freeWrap}>
          <label htmlFor={textId} className={styles.srOnly}>
            Signatur
          </label>
          <textarea
            id={textId}
            className={clsx(styles.textarea, mobile && styles.textareaMobile, error && styles.textareaError)}
            value={value.text}
            rows={mobile ? 6 : 5}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? errorId : undefined}
            onChange={(event) => onChange({ ...value, text: event.target.value })}
          />
          {error ? (
            <span id={errorId} className={styles.error}>
              {error}
            </span>
          ) : null}
          {shown.footer ? <div className={styles.freeFooter}>{shown.footer}</div> : null}
        </div>
      )}
      <div className={clsx(styles.checks, mobile && styles.checksMobile)}>
        <Checkbox
          label="Pflichtangaben der Firma anhängen"
          checked={value.mandatoryDetails}
          onChange={(event) => onChange({ ...value, mandatoryDetails: event.target.checked })}
        />
        {mobile ? null : (
          <Checkbox
            label="Profilbild einbetten"
            checked={value.embedImage}
            onChange={(event) => onChange({ ...value, embedImage: event.target.checked })}
          />
        )}
      </div>
    </div>
  );
}

/** Nutzer 09 right column: „Vorschau im Mailfenster" with a sample recipient and the first two lines. */
export function SignatureMailPreview({ lines, imageUrl }: { lines: string[]; imageUrl: string | null }) {
  return (
    <div className={styles.previewCard}>
      <span className={styles.previewTitle}>Vorschau im Mailfenster</span>
      <div className={styles.mail}>
        <div className={styles.mailHead}>An: kundin@beispiel.de</div>
        <div className={styles.mailBody}>
          <span className={styles.mailLine} style={{ width: '86%' }} aria-hidden="true" />
          <span className={styles.mailLine} style={{ width: '72%' }} aria-hidden="true" />
          <span className={styles.mailRule} aria-hidden="true" />
          <span className={styles.mailSignature}>
            {imageUrl ? <img src={imageUrl} alt="" className={styles.mailImage} /> : null}
            <span>
              {lines.slice(0, 2).map((line, index) => (
                <span key={index} className={styles.mailSignatureLine}>
                  {line}
                </span>
              ))}
            </span>
          </span>
        </div>
      </div>
    </div>
  );
}
